import moment from 'moment-timezone';

import { Account } from '@models/account/account.model';
import { User } from '@models/user.model';

import { AccessControlGrantAdapter } from './access-control-grant.adapter';
import { ApiAdapter } from './adapter';
import { NotificationsSettingAdapter } from './notification-setting.adapter';
import { SiteGroupAdapter } from './site-group.adapter';
import { SupplierAdapter } from './supplier.adapter';

export class UserAdapter extends ApiAdapter<User> {
  private notificationsSettingAdapter = new NotificationsSettingAdapter();
  private siteGroupAdapter = new SiteGroupAdapter();
  private supplierAdapter = new SupplierAdapter();
  private accessControlGrantAdapter = new AccessControlGrantAdapter();

  get(json): User {
    // Those json attributes are only provided by /customer/me endpoint
    let userPreferences = json['user_preferences'] || {};
    const notificationsSettings = json['notifications_settings'] || [];

    // Initialize user preferences with default values
    userPreferences = {
      ...{
        lang: 'fr',
        hasSound: true,
        siteGroups: [],
        onBoardDate: null,
        preferredCountries: [],
        preferredAccounts: [],
        isTermsOfUseAccepted: false,
        termsOfUseAcceptanceDate: null,
      },
      ...userPreferences,
    };

    return new User({
      id: json['id'],
      email: json['okta_login'],
      apiKey: json['apikey'],
      firstname: json['firstname'] || '',
      lastname: json['lastname'] || '',
      position: json['position'] || '',
      emailSecondary: json['contact_email'],
      isEngie: !json['external'],
      phone: json['primary_phone'],
      phoneSecondary: json['secondary_phone'],
      accounts: json['external_accounts']
        ? json['external_accounts'].map((jsonAccount) => new Account({ id: jsonAccount['account_id'] }))
        : undefined,
      hasAvatar: json['has_avatar'],
      siteGroups: this.siteGroupAdapter.list(userPreferences['siteGroups']),
      lang: userPreferences['lang'],
      hasSound: userPreferences['hasSound'],
      onBoardDate: userPreferences['onBoardDate'] ? moment.tz(userPreferences['onBoardDate'], moment.tz.guess()) : null,
      notificationsSettings: this.notificationsSettingAdapter.list(notificationsSettings),
      // Backend may send a supplier attribute with all sub attributes to null!
      supplier: json['supplier'] && json['supplier']['id'] ? this.supplierAdapter.get(json['supplier']) : undefined,
      accessControlGrants: this.accessControlGrantAdapter.list(json['grants']),
      preferredCountries: userPreferences['preferredCountries'],
      preferredAccounts: userPreferences['preferredAccounts'],
      isTermsOfUseAccepted: userPreferences['isTermsOfUseAccepted'],
      termsOfUseAcceptanceDate: userPreferences['termsOfUseAcceptanceDate'],
      allowedCountries: json['countries'],
      isActive: this.isUserActive(json),
      contractNotifications: json['transactions_confirmations_contracts']?.map((tx) => tx['contract_name']),
    });
  }

  private isUserActive(json): boolean {
    // to make the call lighter, we do not want the field is_active in the response for internal contacts
    // as the default param is active_only = true, so normally the answer is true, but just in case.
    return json['is_active'] || true;
  }
}
